import * as config from './config';
import * as dm from './domain';
import * as utils from './utils';
import DomainModel from './domain-model';
import animateOpen from './animation/animate-open';
import toTechnicalDocument from './view3d/to-technical-document';
import traverse from "./control-flow/traverse-generator";
import { CameraHelper, DirectionalLight, DirectionalLightHelper, Mesh, Object3D } from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { saveBuffer } from './utils/save-buffer';
import { sortedByConnection } from './controllers/wall-controllers';

const ln = (str: string, indent: number) => {
    return '\t'.repeat(indent) + str + '\n';
}

export const toGlbFile = (obj: Object3D): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        let exporter = new GLTFExporter();
        exporter.parse(
            obj,
            (glb: ArrayBuffer) => resolve(glb),
            error => reject(error),
            { binary: true }
        );
    });
}


const gltfLoader = new GLTFLoader();

const importFileAsChild = (file: File, scene: Object3D): Promise<Object3D> => {
    const url = URL.createObjectURL(file);

    return new Promise((resolve, reject) => {
        gltfLoader.load(
            url,
            gltf => {
                const obj = gltf.scene;
                scene.add(obj);
                obj.traverse(child => {
                    if(child instanceof Mesh) {
                        child.castShadow = child.receiveShadow = true;
                    }
                });
                resolve(obj);
            },
            null, // no progress callback
            error => {
                console.error(error);
                reject(error)
            }
        );
    })
}

const array = (property: string, model: any, indent: number) => {
    let result = '';
    if(model[property] && model[property].length > 0) {
        result += ln(`${property} (${model[property].length})`, indent);
        for(let child of model[property]) {
            result += describe(child, indent+1);
        }
    }
    return result;
}

const vecln = (property: string, model: any, indent: number) => {
    let result = '';
    const {x, y, z} = model[property] as {x: number, y: number, z: number};
    result += ln(`${property}: ${x.toFixed(3)}, ${y.toFixed(3)}, ${z.toFixed(3)}`, indent);
    return result;
}

export const describe = (model: any, indent: number = 0): string => {
    let result = "";
    switch(model.constructor) {
        case dm.ClosetUnit:
            result += ln('ClosetUnit', indent++);
            result += describe(model.content, indent+1);
            break;
        case dm.Section:
            result += ln('Section', indent++);
            result += ln(`width: ${model.width}`, indent);
            result += ln(`height: ${model.height}`, indent);
            result += ln(`elevation: ${model.position?.y}`, indent);
            result += array('sections', model, indent);
            result += array('parametricParts', model, indent);
            result += array('drawers', model, indent);
            result += array('assets', model, indent);
           
            if(model.door) {
                result += describe(model.door, indent);
            }
            break;
        case dm.ParametricPart:
            result += ln(`ParametricPart (${model.name})`, indent);
            break;
        case dm.Drawer:
            result += ln(`Drawer (${model.height}")`, indent);
            break;
        case dm.GenericAsset:
            result += ln('GenericAsset', indent++);
            result += ln(`filename: ${model.filename}`, indent);
            result += ln(`height: ${model.height}`, indent);
            result += ln(`position.y: ${model.position.y}`, indent);
            break;
        case dm.Door:
            result += ln(`Door (${model.hinge})`, indent++);
            result += ln(`width: ${model.width}`, indent);
            result += array('handles', model, indent);
            break;
        case dm.Handle:
            result += ln(`Handle`, indent++);
            result += ln(`side: ${model.side}`, indent);
            result += vecln('position', model, indent);
    }
    return result;
}

(self as any).dev = {

    tree() {
        const app: any = (self as any).app;
        console.log(describe(app.model));
    },

    // store a design in local storage under the given name
    store(name: string) {
        if(!name) {
            console.error(`Can't store with name "${name}"`);
            return;
        }
        const app: any = (self as any).app;
        const str = localStorage.getItem('CPS_unit_dev_stored_units');
        if(str) {
            const data = JSON.parse(str);
            data[name] = app.model.toJSON();
            localStorage.setItem('CPS_unit_dev_stored_units',
                                 JSON.stringify(data));
        } else {
            localStorage.setItem('CPS_unit_dev_stored_units',
                                 JSON.stringify({
                                     [name]: app.model.toJSON()
                                 }));
        }
    },

    // lists available stored designs
    stored() {
        const str = localStorage.getItem('CPS_unit_dev_stored_units');
        if(str) {
            const data = JSON.parse(str);
            return Array.from(Object.keys(data));
        } else {
            return [];
        }
    },

    load(name: string) {
        const app: any = (self as any).app;
        const str = localStorage.getItem('CPS_unit_dev_stored_units');
        if(str) {
            const data = JSON.parse(str);
            if(name in data) {
                app.model = dm.ClosetUnit.fromJSON(data[name]);
                app.refresh();
            } else {
                console.error(`No stored unit named "${name}"`);
            }
        } else {
            console.error('No stored units');
        }
    },

    updateLoopRunning: false,

    startUpdateLoop() {
        const app: any = (self as any).app;
        this.updateLoopRunning = true;
        const animate = () => {
            if(this.updateLoopRunning) {
                requestAnimationFrame(animate);
                app.pipeline.update = true;
                console.log('update');
            }
        }
        animate();
    },

    stopUpdateLoop() {
        this.updateLoopRunning = false;
    },

    animateOpen(amount: number, model: DomainModel) {
        if('open' in model) {
            animateOpen(amount, model as any);
        }
    },

    techDoc(obj: Object3D, doorOpacity: number = 1) {
        toTechnicalDocument(obj, false, doorOpacity);
    },

    async export(obj: Object3D) {
        const buffer = await toGlbFile(obj);
        saveBuffer(buffer, 'export.glb');
    },

    /**
     * Console usage: [model] = await importFileTo(scene)
     *
     * Shows a file prompt to select one or more .glb or .gltf files.
     *
     * This returns the model object(s) as well as appending them to the given
     * object, which is usually going to be the scene. You can do many files
     * at once by selecting multiple in the file dialog and you'll get all the
     * files back as an arry.
     */
    async importFileTo(scene: Object3D): Promise<Object3D[]> {
        const
            promises: Promise<Object3D>[] = [],
            input = document.createElement('input');
        input.type = 'file';
        input.accept = '.glb,.gltf';
        input.multiple = true;

        const inputChanged: Promise<void> = new Promise(resolve => {
            input.onchange = (event: Event) => {
                for(const file of input.files) {
                    promises.push(importFileAsChild(file, scene));
                }
                resolve();
            }
        });

        input.click();

        await inputChanged;

        const results = await Promise.allSettled(promises);
        return results
            .filter(result => result.status == "fulfilled")
            .map((result: PromiseFulfilledResult<Object3D>) => result.value);
    },

    directionalLightHelper(root: Object3D) {
        for(const obj of traverse(root)) {
            if(obj instanceof DirectionalLight) {
                const helper = new DirectionalLightHelper(obj);
                root.add(helper);

                if(obj.castShadow) {
                    const camera = obj.shadow.camera;
                    const helper = new CameraHelper(camera);
                    root.add(helper);
                }
            }
        }
    },


    sortWallsByConnection(walls: dm.Wall[]) {
        utils.shuffle(walls);
        return sortedByConnection(walls)
    },

    setupModel(overrides: any) {
        const
            app = (self as any).app,
            data = {
                layout: "U-Shape",
                island: false,
                style: "PE",
                pull: "pull1",
                wall1: 150,
                wall2: 192,
                wall3: 150,
            },
            selectionString = localStorage.getItem('kitchen-tool-selections'),
            selection = JSON.parse(selectionString);

        Object.assign(data, selection);
        Object.assign(data, overrides);
        localStorage.setItem('kitchen-tool-selections', JSON.stringify(data));

        app.common.setupModel();
        app.refresh();
    },

    dump(obj: Object3D, indent: number = 0) {
        const leader = "\t".repeat(indent);
        console.log(`${leader}${obj.constructor.name} "${obj.name ?? ''}"`);
        for(const child of obj.children) {
            this.dump(child, indent+1);
        }
    }
}

